import React, {useCallback, useMemo, useState} from 'react';
import {createPortal} from 'react-dom';

import useOnResize from 'lib/hooks/useOnResize';

import {useAnnotationModeStore} from './AnnotationModeStore';
import AnnotationNoteIndicator from './AnnotationNoteIndicator';
import {resolveIndicatorScroller, selfAndAncestorsThrough} from './annotationIndicatorPlacement';
import type {AnnotationNoteLine} from './annotationNoteLines';
import {measureNoteLines} from './annotationNoteLines';
import {filterNotedAnnotations} from './annotationSelectors';
import type {TextProjection} from './annotationTextProjection';
import type {Annotation} from './annotationTypes';
import useOnRegionSettle from './useOnRegionSettle';

const NO_ANNOTATIONS: Annotation[] = [];
const NO_TARGETS: Element[] = [];

interface AnnotationNoteIndicatorsProps {
  regionId: string;
  /** The region element; null until it attaches. */
  container: HTMLElement | null;
  getProjection: () => TextProjection | null;
}

interface IndicatorPlacement {
  scroller: HTMLElement;
  lines: AnnotationNoteLine[];
}

/**
 * The region's noted marks, each flagged by a button in the margin of the pane
 * that scrolls it. They portal into that pane and position absolutely, so they
 * ride the text rather than the viewport.
 */
export default function AnnotationNoteIndicators({
  regionId,
  container,
  getProjection
}: AnnotationNoteIndicatorsProps) {
  const annotations = useAnnotationModeStore(
    (state) => state.annotationsByRegionId[regionId] ?? NO_ANNOTATIONS
  );
  const [placement, setPlacement] = useState<IndicatorPlacement | null>(null);

  const measure = useCallback(() => {
    const notedAnnotations = filterNotedAnnotations(annotations);
    const scroller = resolveIndicatorScroller(container);
    const projection = notedAnnotations.length > 0 ? getProjection() : null;
    if (!scroller || !projection) {
      setPlacement(null);
      return;
    }
    setPlacement({scroller, lines: measureNoteLines(notedAnnotations, projection, scroller)});
  }, [annotations, container, getProjection]);

  // A line's top moves when anything between it and the pane changes height, and a
  // pinned ancestor (`h-full`) can hide that from any single box. Watch the chain.
  const resizeTargets = useMemo(() => {
    const scroller = resolveIndicatorScroller(container);
    return container && scroller ? selfAndAncestorsThrough(container, scroller) : NO_TARGETS;
  }, [container]);

  useOnResize(resizeTargets, measure);
  // A mark stops resolving when the text it anchors to changes, which needn't
  // resize anything — so the text itself has to be watched as well.
  useOnRegionSettle(container, measure);

  if (!placement) {
    return null;
  }
  return createPortal(
    placement.lines.map((line) => (
      <AnnotationNoteIndicator key={line.annotations[0].id} line={line} />
    )),
    placement.scroller
  );
}
